import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../hooks/useSEO';
import { allTools } from '../../mocks/tools';

const categoryDefs = [
  {
    id: '1',
    name: 'AI Writing Tools',
    description: 'Generate blog posts, marketing copy, emails, and content with AI-powered writing assistants.',
    icon: 'ri-quill-pen-line',
    slug: 'ai-writing-tools',
  },
  {
    id: '2',
    name: 'AI Image Generators',
    description: 'Create stunning visuals, art, and graphics from simple text descriptions using AI.',
    icon: 'ri-image-line',
    slug: 'ai-image-generators',
  },
  {
    id: '3',
    name: 'AI Video Tools',
    description: 'Edit, generate, and enhance videos with AI-powered video creation platforms.',
    icon: 'ri-video-line',
    slug: 'ai-video-tools',
  },
  {
    id: '4',
    name: 'AI Website Builders',
    description: 'Build professional websites in minutes with AI-powered design and content generation.',
    icon: 'ri-layout-line',
    slug: 'ai-website-builders',
  },
  {
    id: '5',
    name: 'AI Marketing Tools',
    description: 'Automate marketing campaigns, email outreach, and customer engagement with AI.',
    icon: 'ri-megaphone-line',
    slug: 'ai-marketing-tools',
  },
  {
    id: '6',
    name: 'AI Productivity Tools',
    description: 'Boost efficiency with AI assistants, automation, and workflow optimization tools.',
    icon: 'ri-rocket-line',
    slug: 'ai-productivity-tools',
  },
  {
    id: '7',
    name: 'AI Coding Tools',
    description: 'Write code faster with AI pair programmers and intelligent code completion.',
    icon: 'ri-code-line',
    slug: 'ai-coding-tools',
  },
  {
    id: '8',
    name: 'AI Design Tools',
    description: 'Create professional designs, logos, and graphics with AI-powered design platforms.',
    icon: 'ri-palette-line',
    slug: 'ai-design-tools',
  },
];

export default function CategoriesPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://toolnova.ai';

  useSEO({
    title: 'Browse AI Tools by Category 2026 — ToolNova',
    description: 'Explore the best AI tools organized by category. Writing, Image, Video, Coding, Design, Marketing, Productivity, and Website Builder AI tools — all in one place.',
    keywords: 'AI tools categories, AI writing tools, AI image generators, AI video tools, AI coding tools',
    canonical: `${siteUrl}/categories`,
    ogTitle: 'Browse AI Tools by Category — ToolNova',
    ogDescription: 'Find the perfect AI tool for your needs. Browse 8 categories covering Writing, Image, Video, Coding, Design, Marketing, Productivity, and more.',
    ogType: 'website',
    ogUrl: `${siteUrl}/categories`,
    ogImage: 'https://public.readdy.ai/ai/img_res/983de63e74833efe3152417c5eeb81b3.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'AI Tool Categories',
      description: 'Browse the best AI tools organized by category on ToolNova.',
      url: `${siteUrl}/categories`,
    },
  });

  const categoriesWithCounts = useMemo(() => {
    return categoryDefs.map((cat) => ({
      ...cat,
      toolCount: allTools.filter((t) => t.category === cat.name).length,
    }));
  }, []);

  const totalTools = useMemo(() => allTools.length, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />

      {/* ── Hero ── */}
      <section className="relative pt-28 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-teal-500/8 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gray-700/60 to-transparent"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8">
            <Link to="/" className="text-gray-500 hover:text-teal-400 transition-colors duration-200 cursor-pointer">
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-gray-700 w-4 h-4 flex items-center justify-center"></i>
            <span className="text-gray-300 font-medium">Categories</span>
          </nav>

          <span className="inline-block px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-teal-400 text-xs font-semibold tracking-widest uppercase mb-6">
            AI Tool Directory
          </span>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white mb-6 tracking-tight leading-tight">
            Browse AI Tools<br />
            <span className="text-teal-400">by Category</span>
          </h1>

          <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Explore {totalTools}+ hand-picked AI tools organized by use case. Find exactly what you need across writing, design, video, coding, marketing, and more.
          </p>

          <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-teal-400"></span>
              {categoriesWithCounts.length} Categories
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-teal-400"></span>
              {totalTools}+ Tools Reviewed
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-teal-400"></span>
              Updated 2026
            </span>
          </div>
        </div>
      </section>

      {/* ── Categories Grid ── */}
      <section className="px-4 sm:px-6 lg:px-8 pb-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {categoriesWithCounts.map((cat) => (
              <CategoryPageCard key={cat.id} category={cat} />
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 text-center">
            <p className="text-gray-400 text-sm mb-6">
              Can&apos;t find what you&apos;re looking for? Browse the full directory.
            </p>
            <Link
              to="/#tools-directory"
              className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 hover:bg-teal-400 text-white font-semibold rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg shadow-teal-500/25 hover:shadow-teal-400/40 hover:scale-105 active:scale-95 text-sm"
            >
              <i className="ri-compass-3-line text-base"></i>
              Browse All Tools
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ── Inner card component ── */
interface CategoryPageCardProps {
  category: {
    id: string;
    name: string;
    description: string;
    icon: string;
    slug: string;
    toolCount: number;
  };
}

function CategoryPageCard({ category }: CategoryPageCardProps) {
  return (
    <Link
      to={`/category/${category.slug}`}
      className="relative flex flex-col bg-[#111827] border border-gray-800/80 rounded-2xl p-5 sm:p-7 hover:border-teal-500/40 transition-all duration-300 cursor-pointer group hover:shadow-[0_0_30px_rgba(20,184,166,0.12)] hover:scale-[1.02] overflow-hidden"
      style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
    >
      {/* Top glow edge */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-teal-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

      {/* Subtle radial glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-teal-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl"></div>

      {/* Icon */}
      <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-teal-500/10 border border-teal-500/25 rounded-xl mb-4 group-hover:bg-teal-500/20 group-hover:border-teal-500/50 group-hover:shadow-[0_0_16px_rgba(20,184,166,0.25)] transition-all duration-300 shrink-0">
        <i className={`${category.icon} text-2xl sm:text-3xl text-teal-300`}></i>
      </div>

      {/* Tool count badge */}
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-500/10 border border-teal-500/20 text-teal-400 text-[11px] font-semibold mb-3 w-fit">
        <i className="ri-tools-line text-[10px]"></i>
        {category.toolCount} Tools
      </span>

      {/* Name */}
      <h2 className="text-sm sm:text-base font-bold text-white mb-2 group-hover:text-teal-300 transition-colors duration-200 leading-snug">
        {category.name}
      </h2>

      {/* Description */}
      <p className="text-gray-400 text-xs sm:text-sm leading-relaxed flex-1 mb-4">
        {category.description}
      </p>

      {/* Arrow */}
      <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-800/60">
        <span className="text-xs text-gray-500 group-hover:text-teal-400 transition-colors duration-200 font-medium">
          Explore category
        </span>
        <span className="w-7 h-7 flex items-center justify-center rounded-full bg-teal-500/10 group-hover:bg-teal-500/25 group-hover:scale-110 transition-all duration-300">
          <i className="ri-arrow-right-line text-teal-300 text-sm group-hover:translate-x-0.5 transition-transform duration-200"></i>
        </span>
      </div>
    </Link>
  );
}
